<script setup lang="ts">
import t from "~/utils/translate";
import { useSettings } from "~/store/settings";
import { useBetbook } from "~/store/betbook";
import { IEvent } from "~/types/sports/event";
import { IMarket } from "~/types/sports/market";
import { IOutcome } from "~/types/sports/outcome";
import { IPrice } from "~/types/sports/price";
import { IMarketGroup } from "~/types/racing/market-group";
import { useQuery } from "@tanstack/vue-query";
import { mdiChevronRight } from "@mdi/js";
import api from "~/utils/auth/api";
import RegionFlag from "~/components/layout/TrackRacing/Shared/Region/Flag.vue";
import { signalREndpoints, useSignalR } from "~/composables/SignalR/useSignalR";
import BetbookSignalRCallback from "~/composables/SignalR/callbacks/BetbookSignalRCallback";
import NextOffLoading from "./NextOffLoading.vue";
import ExpandableOutcomeRow from "~/components/layout/TrackRacing/Shared/Outcome/ExpandableOutcomeRow.vue";
import OutcomeRow from "~/components/layout/TrackRacing/Shared/Outcome/OutcomeRow.vue";
import useSignalRRecconect from "~/composables/useSignalRRecconect";
import { isMobile } from "~/utils/constants";

const settings = useSettings();
const route = useRoute();
export interface INextOffRaces {
	events: IEvent[];
	marketGroups: IMarketGroup[];
	markets: IMarket[];
	outcomes: IOutcome[];
	prices: IPrice[];
}
export interface ILocalEmop extends IEvent {
	market: IMarket | undefined;
	outcomes: IOutcome[] | [];
	numberOfRunners: number | 0;
}

const { $checkSignalRConnectionStatus } = useNuxtApp();
const take = ref<number>(9);
const isVirtual = ref<boolean>(false);
const marketType = ref<string>("Race Winner");
const marketGroupname = ref<string>("Race Winner");
const betbookSignalR = useSignalR("betBook", BetbookSignalRCallback);
const abortController = new AbortController();

const sportId = computed(() => {
	return route.params.sportId;
});

const store = useBetbook();
const emit = defineEmits(["event-selected"]);

const handleSubscription = async (response: INextOffRaces) => {
	await $checkSignalRConnectionStatus("TrackRacing");
	const uniqueMarkets = new Set(response.markets.map((x) => x.name));
	const eventIds = response.events.map((e) => e.eventId);
	betbookSignalR.subscribe(signalREndpoints.subscriptionBetbookEndpoint, eventIds, {
		eventIds,
		marketTypes: [...uniqueMarkets].map((x) => x),
		countryCode: settings.config?.appSetting?.CountryCode,
	});
};

const nextOffApiCall = () => {
	return api<INextOffRaces>({
		url: `/TrackRacing/GetNextOff?sportId=${sportId.value}&take=${take.value}&isVirtual=${isVirtual.value}&marketType=${marketType.value}&marketGroupname=${marketGroupname.value}&countryCode=${settings.config?.appSetting?.CountryCode}`,
		type: "horses",
		apiVersion: "v1",
		signal: abortController.signal,
		options: { method: "GET" },
	}).then(async (response) => {
		try {
			await Promise.all([
				store.setEvents(response.events),
				store.setMarkets(response.markets),
				store.setOutcomes(response.outcomes),
				store.setPrices(response.prices),
			]);
			handleSubscription(response);
		} catch (err) {
			console.error(err);
		}
		return response;
	});
};

const {
	data: nextOffEvents,
	refetch: getNextOff,
	isLoading,
	isFetching,
} = useQuery<INextOffRaces>({
	queryKey: ["GetNextOff", sportId, take, isVirtual, marketType, marketGroupname],
	refetchOnReconnect: true,
	queryFn: () => nextOffApiCall(),
});

const eventsOutcomes: ComputedRef<ILocalEmop[] | undefined> = computed(() => {
	const eventsMap = store.events;
	const outcomesMap = store.outcomes;
	const marketsMap = store.markets;

	return Array.from(eventsMap.values()).map((event) => {
		const market = Array.from(marketsMap.values()).find((m) => m.eventId === event.eventId && m.name.includes("Race Winner"));
		const marketOutcomes = Array.from(outcomesMap.values()).filter(
			(outcome) => outcome.eventId === event.eventId && outcome.marketId === market?.marketId && outcome.isTradingActive
		);

		return {
			...event,
			market,
			outcomes: marketOutcomes.slice(0, 3),
			numberOfRunners: marketOutcomes.length,
		};
	});
});

const storedNextOffEvents = computed(() => {
	return [...store.events.values()];
});

// watch(
// 	storedNextOffEvents,
// 	(newEvents) => {
// 		const isSuspendedEvent = !!newEvents && newEvents.some((q) => q.shouldDisplay === false);
// 		if (isSuspendedEvent) {
// 			//Will need to re-look at this
// 			// getNextOff();
// 		}
// 	},
// 	{ deep: true }
// );

const ledger = useLedger();
const handleSignalRunsubscribe = () => {
	const ledgerData = ledger.get("events", "betBook");
	if (ledgerData) {
		betbookSignalR.unSubscribe(signalREndpoints.unsubscriptionBetbookEndpoint, {
			eventIds: ledgerData.events,
			marketTypes: ledgerData.markets,
			countryCode: settings.config?.appSetting?.CountryCode,
		});
	}
};
onBeforeUnmount(() => {
	handleSignalRunsubscribe();
});

onUnmounted(() => {
	if (sportId.value !== "horse-racing") {
		abortController.abort();
	}
});

const handleReconnect = () => {
	handleSignalRunsubscribe();
	getNextOff();
};

useSignalRRecconect(handleReconnect);
</script>
<template>
	<div>
		<NextOffLoading v-if="isLoading || isFetching" />
		<template v-else>
			<div v-if="nextOffEvents?.events && nextOffEvents?.events.length > 0 && !isLoading" class="grid grid-cols-1 md:grid-cols-3 gap-2">
				<div v-for="event in eventsOutcomes" class="text-dark-800 bg-light-50 dark:bg-dark-800 rounded-lg leading-tight">
					<div class="bg-dark-800 dark:bg-dark-700 text-light-50 text-sm rounded-t-lg p-[2px] px-[6px]">
						<div class="flex justify-between items-center">
							<div>
								<div class="flex items-center">
									<RegionFlag :region-id="event.regionId" :width="20" :height="20" />
									<div class="mt-[2px] ml-1 text-xs font-bold">{{ event.league }}</div>
								</div>
								<div class="flex text-xs leading-none mt-[2px]">
									<div class="bg-primary-50 text-dark-800 w-[max-content] rounded-lg p-1 mb-1">
										<UiDateTime
											v-if="event?.venueEpoch"
											class="font-bold"
											dateFormat="dd MMM -"
											timeFormat=" HH:mm"
											time
											:date="event.venueEpoch"
											string-separator=" -"
										/>
									</div>
								</div>
							</div>
							<div class="mr-4 text-xs font-bold">
								{{ t("win") }}
							</div>
						</div>
					</div>
					<div v-if="event.outcomes.length > 0" class="text-xs rounded-lg leading-tight">
						<div
							class="bg-light-50 dark:bg-dark-800 base-text border border-[transparent] border-b-light-300 dark:border-b-dark-700"
							v-for="outcome in event.outcomes"
						>
							<ExpandableOutcomeRow
								v-if="isMobile && sportId !== 'greyhounds'"
								:outcome="outcome"
								:markets="event.market ? [event.market] : []"
								:selected-event-id="event.eventId"
								:is-starting-price="!!event.market && event.market.additionalInfo?.UseFixedPrice === 'False'"
								:sport-id="sportId.toString()"
							/>
							<OutcomeRow
								v-else
								:outcome="outcome"
								:markets="event.market ? [event.market] : []"
								:event-id="event.eventId ?? +event.eventId"
								:show-additional-info="false"
								:is-starting-price="!!event.market && event.market.additionalInfo?.UseFixedPrice === 'False'"
								:sport-id="sportId.toString()"
							/>
						</div>

						<UiButton
							class="w-full text-xs dark:text-light-50"
							:label="`${t('see-more-details')} (${event.numberOfRunners} ${t('runners')})`"
							:icon="mdiChevronRight"
							iconPos="right"
							variant="custom"
							size="medium"
							@click="emit('event-selected', { dailySelection: 'today', eventId: event.eventId, regionId: event.regionId, league: event.league })"
						/>
					</div>
					<div class="text-center text-xs text-dark-800 dark:text-light-50 font-bold p-3" v-else>
						{{ t("horses-no-markets-message") }}
					</div>
				</div>
			</div>
			<div v-else-if="!isLoading" class="w-full text-center text-sm font-bold my-1">
				{{ t("no-events-available") }}
			</div>
		</template>
	</div>
</template>
